import axios from 'axios';
import { User } from '@/store/modules/user';
import _ from 'lodash';

const API_ENDPOINT = process.env.API_ENDPOINT;
const instance = axios.create({
  timeout: 15000,
  withCredentials: true,
});

async function makeSignInRequest(
  email: string,
  password: string,
  rememberMe: boolean,
): Promise<User> {
  const params = new URLSearchParams();

  params.append('email', email);
  params.append('password', password);
  if (rememberMe) {
    params.append('remember_me', '1');
  }

  try {
    const response = await instance.post(`${API_ENDPOINT}/api/login`, params);
    const success = response.data.success;
    if (success === true) {
      const user = response.data.user;
      if (user != undefined) {
        return {
          ...user,
          organizationId: _.get(user, 'organization.id', undefined),
        };
      } else {
        throw 'Unexpected error occurred';
      }
    } else {
      throw response.data.error;
    }
  } catch (err) {
    throw err;
  }
}

async function makeSignUpRequest(
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  invitationCode: string | undefined,
  locale: string
): Promise<User> {
  const params = new URLSearchParams();

  params.append('firstname', firstName);
  params.append('lastname', lastName);
  params.append('email', email);
  params.append('password', password);
  if (invitationCode != undefined) {
    params.append('invitationCode', invitationCode);
  }
  params.append('locale', locale);

  try {
    const response = await instance.post(`${API_ENDPOINT}/api/signup`, params);
    const success = response.data.success;
    if (success === true) {
      const user = response.data.user;
      if (user != undefined) {
        return {
          ...user,
          organizationId: _.get(user, 'organization.id', undefined),
        };
      } else {
        throw new Error('Unexpected error occurred');
      }
    } else {
      throw new Error(
        (response.data.error !== undefined)
          ? response.data.error
          : 'Unexpected error occurred'
      );
    }
  } catch (err) {
    throw err;
  }
}

async function makeSignOutRequest(): Promise<void> {
  try {
    await instance.get(`${API_ENDPOINT}/api/logout`);
    return undefined;
  } catch (err) {
    throw err;
  }
}

async function signIn(email: string, password: string, rememberMe: boolean): Promise<User> {
  try {
    await makeSignOutRequest();
    return await makeSignInRequest(email, password, rememberMe);
  } catch (err) {
    throw err;
  }
}

async function signUp(firstName: string, lastName: string, email: string, password: string, invitationCode: string | undefined, locale: string): Promise<User> {
  try {
    await makeSignOutRequest();
    return await makeSignUpRequest(firstName, lastName, email, password, invitationCode, locale);
  } catch (err) {
    throw err;
  }
}

async function signOut() {
  try {
    await makeSignOutRequest();
  } catch (err) {
    throw err;
  }
}

export { signIn, signUp, signOut };
